import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';

import { DemographicDecision, DemographicProposal, PracticeEntryForReview } from '../models/referral.model';
import { PatientAppConsentRequest } from '../models/patient-app-consent';
import { HistoryDecision, PatientAppHistoryProposal } from '../models/patient-app-history';

/** What can be sent to a patient's app (tasks/patient-app.md §6.1, P14, P17). */
export type PatientAppSourceType = 'note' | 'document' | 'patient_letter' | 'discharge_summary';

export interface PatientAppShare {
  sourceType: PatientAppSourceType;
  sourceId: string;
  audience: 'all' | 'patient_only';
  sharedAt: string;
  withdrawnAt: string | null;
  lastSentAt: string | null;
}

export interface PatientAppShares {
  linked: boolean;
  shares: PatientAppShare[];
  canShare: boolean;
  canWithdraw: boolean;
}

/** An entry the patient shared from their app, shaped as a practice entry so the Inbox reuses that dialog. */
export interface PatientAppEntryForReview extends Omit<PracticeEntryForReview, 'sourceType'> {
  sourceType: 'patient';
  kind: string;
  /** The patient's list this entry can also be filed into (an allergy, condition, surgery or medication), or null. */
  listTarget?: string | null;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

/**
 * The patient app, as the practice uses it (tasks/patient-app.md §6, §5.2): sending entries to a linked patient's app,
 * and reviewing in the Inbox what the patient sent (their own entries, changes to their details).
 */
@Injectable({ providedIn: 'root' })
export class PatientAppService {
  private apiUrl = 'api';

  constructor(private http: HttpClient) {}

  private authOptions() {
    return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  }

  /* ── sending to the patient's app ─────────────────────────────────────────────── */

  listShares(patientId: string): Observable<PatientAppShares> {
    return this.http.get<PatientAppShares>(`${this.apiUrl}/patients/${patientId}/patient-app/shares`, this.authOptions());
  }

  share(patientId: string, sourceType: PatientAppSourceType, sourceId: string, audience: 'all' | 'patient_only'): Observable<{ shareId: string; alreadyShared: boolean }> {
    return this.http.post<{ shareId: string; alreadyShared: boolean }>(`${this.apiUrl}/patients/${patientId}/patient-app/shares`, { sourceType, sourceId, audience }, this.authOptions());
  }

  withdraw(patientId: string, sourceType: PatientAppSourceType, sourceId: string): Observable<{ withdrawn: boolean }> {
    return this.http.post<{ withdrawn: boolean }>(`${this.apiUrl}/patients/${patientId}/patient-app/shares/withdraw`, { sourceType, sourceId }, this.authOptions());
  }

  /** P13: take back a mirrored email's app copy. The email itself was already sent and cannot be recalled. */
  withdrawMail(patientId: string, mailId: string): Observable<{ withdrawn: boolean }> {
    return this.http.post<{ withdrawn: boolean }>(`${this.apiUrl}/patients/${patientId}/patient-app/mail/${mailId}/withdraw`, {}, this.authOptions());
  }

  /* ── consents to sign in the patient's app (patient-platform tasks/practice-requests.md) ── */

  /** What became of each consent sent to the patient's app, and whether the file is linked. Opened automatically
   *  when a consent invite is created or re-sent (patient-platform tasks/patient-email-inbox.md "Consents follow
   *  the email"); there is no separate send from here any more. */
  consentRequests(patientId: string): Observable<{ linked: boolean; requests: PatientAppConsentRequest[] }> {
    return this.http.get<{ linked: boolean; requests: PatientAppConsentRequest[] }>(`${this.apiUrl}/patients/${patientId}/patient-app/consent-requests`, this.authOptions());
  }

  /* ── the Inbox ────────────────────────────────────────────────────────────────── */

  getInboundEntry(entryId: string, groupId: string): Observable<PatientAppEntryForReview> {
    return this.http.get<Envelope<PatientAppEntryForReview>>(`${this.apiUrl}/patient-app/group/${groupId}/entries/${entryId}`, this.authOptions()).pipe(map((r) => r.data));
  }

  previewInboundFile(entryId: string, fileId: string, groupId: string): Observable<{ url: string }> {
    return this.http.get<Envelope<{ url: string }>>(`${this.apiUrl}/patient-app/group/${groupId}/entries/${entryId}/files/${fileId}/preview`, this.authOptions()).pipe(map((r) => r.data));
  }

  /** `fileToList`: also file an allergy, condition, surgery or medication into the patient's list of it. */
  validateInboundEntry(entryId: string, groupId: string, fileToList = false): Observable<{ patientNoteId: string; alreadyFiled: boolean; filedToList: string | null }> {
    return this.http.post<Envelope<{ patientNoteId: string; alreadyFiled: boolean; filedToList: string | null }>>(
      `${this.apiUrl}/patient-app/group/${groupId}/entries/${entryId}/validate`, { fileToList }, this.authOptions()).pipe(map((r) => r.data));
  }

  dismissInboundEntry(entryId: string, reason: string, groupId: string): Observable<{ alreadyDismissed: boolean }> {
    return this.http.post<Envelope<{ alreadyDismissed: boolean }>>(`${this.apiUrl}/patient-app/group/${groupId}/entries/${entryId}/dismiss`, { reason }, this.authOptions()).pipe(map((r) => r.data));
  }

  getDetailProposal(proposalId: string, groupId: string): Observable<DemographicProposal> {
    return this.http.get<Envelope<DemographicProposal>>(`${this.apiUrl}/patient-app/group/${groupId}/proposals/${proposalId}`, this.authOptions()).pipe(map((r) => r.data));
  }

  resolveDetailProposal(proposalId: string, decisions: Record<string, DemographicDecision>, groupId: string): Observable<{ applied: string[] }> {
    return this.http.post<Envelope<{ applied: string[] }>>(`${this.apiUrl}/patient-app/group/${groupId}/proposals/${proposalId}/resolve`, { decisions }, this.authOptions()).pipe(map((r) => r.data));
  }

  getHistoryProposal(proposalId: string, groupId: string): Observable<PatientAppHistoryProposal> {
    return this.http.get<Envelope<PatientAppHistoryProposal>>(`${this.apiUrl}/patient-app/group/${groupId}/history/${proposalId}`, this.authOptions()).pipe(map((r) => r.data));
  }

  resolveHistoryProposal(proposalId: string, decisions: Record<string, HistoryDecision>, groupId: string): Observable<{ outcomes: Record<string, string> }> {
    return this.http.post<Envelope<{ outcomes: Record<string, string> }>>(`${this.apiUrl}/patient-app/group/${groupId}/history/${proposalId}/resolve`, { decisions }, this.authOptions()).pipe(map((r) => r.data));
  }
}
